// Marca Lezty Pay: o pião do Lezty em vetor, com os gradientes do canvas Zenith
// (`~/.config/la-mafia/croqui-zenith/components/Logo.tsx`, aprovado em 17/09/2026). O
// wordmark mora aqui porque a nav desenha os dois juntos.
//
// Os hexadecimais dos gradientes são ARTE, não tema: o pião é dourado no claro e no escuro,
// igual a um logotipo em PNG. Por isso são os únicos hex do painel fora de `styles/`.

import { useId } from 'react';
import { cn } from '../../lib/cn.ts';

export const UPPER =
  'M46.8 3.6 A3.2 3.2 0 0 1 53.2 3.6 C53.6 14 54.4 28 57.4 36 C60.5 46 67 56 80 61.5 C89 64.8 96 66 100 67 Q50 72 0 67 C4 66 11 64.8 20 61.5 C33 56 39.5 46 42.6 36 C45.6 28 46.4 14 46.8 3.6 Z';
export const LOWER =
  'M97.5 71.2 C96 78.5 77 83 64 90 C57 95 53.6 101 51.3 107.4 Q50 110 48.7 107.4 C46.4 101 43 95 36 90 C23 83 4 78.5 2.5 71.2 Q50 75.8 97.5 71.2 Z';
const RIM = 'M0 67 Q50 72 100 67 L99.2 70.6 Q50 76 0.8 70.6 Z';

type Stop = [number, string, number?];

function stops(list: Stop[]) {
  return list.map(([offset, color, opacity]) => (
    <stop key={offset} offset={offset} stopColor={color} stopOpacity={opacity ?? 1} />
  ));
}

/**
 * Pião Lezty Pay. `size` é a LARGURA em px; a altura sai de 1,11x (o pião não é quadrado).
 * 64 na sidebar antiga, 44 no rail, 30 na nav nova, 24 inline.
 */
export function Mascot({ size = 64, className }: { size?: number; className?: string }) {
  const raw = useId();
  const id = raw.replace(/[^a-zA-Z0-9_-]/g, '');
  const u = (name: string): string => `lz${id}${name}`;
  return (
    <svg
      width={size}
      height={(size * 111) / 100}
      viewBox="0 0 100 111"
      className={cn('block shrink-0', className)}
      role="img"
      aria-label="Lezty Pay"
    >
      <defs>
        <linearGradient id={u('body')} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="100" y2="0">
          {stops([
            [0, '#5a3b0c'],
            [0.1, '#8f6420'],
            [0.26, '#d6a03f'],
            [0.36, '#f8d98a'],
            [0.46, '#dea647'],
            [0.58, '#9a6b1f'],
            [0.7, '#b07e26'],
            [0.84, '#e9bd5e'],
            [0.94, '#a77927'],
            [1, '#6b470f'],
          ])}
        </linearGradient>
        <linearGradient id={u('stem')} gradientUnits="userSpaceOnUse" x1="42" y1="0" x2="58" y2="0">
          {stops([
            [0, '#6b470f'],
            [0.22, '#b8892f'],
            [0.38, '#fbe3a0'],
            [0.52, '#d8aa4c'],
            [0.72, '#8a5e19'],
            [0.9, '#c79a45'],
            [1, '#7a5313'],
          ])}
        </linearGradient>
        <linearGradient
          id={u('stemFade')}
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="34"
          x2="0"
          y2="56"
        >
          {stops([
            [0, '#fff'],
            [1, '#000'],
          ])}
        </linearGradient>
        <mask id={u('stemMask')}>
          <rect x="0" y="0" width="100" height="72" fill={`url(#${u('stemFade')})`} />
        </mask>
        <linearGradient id={u('rim')} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="100" y2="0">
          {stops([
            [0, '#8d7040'],
            [0.15, '#e8d7ae'],
            [0.4, '#fffaf0'],
            [0.55, '#f4e9cf'],
            [0.8, '#cdb483'],
            [1, '#7d6235'],
          ])}
        </linearGradient>
        <linearGradient
          id={u('lower')}
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2="100"
          y2="0"
        >
          {stops([
            [0, '#4a300a'],
            [0.16, '#8f6420'],
            [0.3, '#d49e3e'],
            [0.4, '#f1cf7c'],
            [0.5, '#c19038'],
            [0.66, '#6f4a12'],
            [0.82, '#b58833'],
            [0.93, '#8a5f1b'],
            [1, '#4e330b'],
          ])}
        </linearGradient>
        <linearGradient
          id={u('lowerShade')}
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="71"
          x2="0"
          y2="84"
        >
          {stops([
            [0, '#2a1803', 0.6],
            [1, '#2a1803', 0],
          ])}
        </linearGradient>
        <linearGradient id={u('spec')} gradientUnits="userSpaceOnUse" x1="0" y1="4" x2="0" y2="60">
          {stops([
            [0, '#fffdf2', 0.95],
            [1, '#fff3d2', 0],
          ])}
        </linearGradient>
      </defs>
      <path d={LOWER} fill={`url(#${u('lower')})`} />
      <path d={LOWER} fill={`url(#${u('lowerShade')})`} />
      <path d={UPPER} fill={`url(#${u('body')})`} />
      <path d={UPPER} fill={`url(#${u('stem')})`} mask={`url(#${u('stemMask')})`} />
      <path
        d="M48.5 5 C48.4 17 47.9 29 46.3 37 C44 46 38 54 25 60.2"
        stroke={`url(#${u('spec')})`}
        strokeWidth="1.3"
        fill="none"
        strokeLinecap="round"
      />
      <ellipse cx="50" cy="63.2" rx="3" ry="1.3" fill="#6b470f" opacity=".3" />
      <ellipse cx="49" cy="62.6" rx="1.3" ry=".55" fill="#fff6d8" opacity=".85" />
      <ellipse
        cx="85"
        cy="64"
        rx="7"
        ry=".9"
        fill="#fff6dc"
        opacity=".6"
        transform="rotate(14 85 64)"
      />
      <path d={RIM} fill={`url(#${u('rim')})`} />
      <path
        d="M0.8 70.6 Q50 76 99.2 70.6"
        stroke="#5c4214"
        strokeOpacity=".55"
        strokeWidth=".5"
        fill="none"
      />
    </svg>
  );
}

/** Wordmark "Lezty Pay", em Inter (a fonte do painel), não em caixa alta espaçada. */
export function Wordmark({ className }: { className?: string }) {
  return <span className={className}>Lezty Pay</span>;
}
